import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import React from "react";
import {
  SavedViewFormSchema,
  SavedViewFormType,
} from "src/hooks/useSavedViews";
import { WEGLOT_APPLY_CLASS } from "src/plugins/weglot/constants";
import { FieldDerivedValue } from "src/services/formikExtra";
import { validateWithZod } from "src/services/formValidations";
import { FormikEmojiInput } from "../Inputs/Formik/FormikEmojiInput";
import { InputWithValidationControl } from "../Inputs/InputWithValidationControl";

const MAX_NAME_LENGTH = 160;

type Props = {
  savedView?: SavedViewFormType;
  isOpen: boolean;
  onCancel: () => void;
  onSave: (savedView: SavedViewFormType) => void;
};
export const ManageSavedViewDialog: React.FC<Props> = ({
  savedView,
  isOpen,
  onCancel,
  onSave,
}) => {
  const handleSave = (
    values: SavedViewFormType,
    { resetForm }: FormikHelpers<SavedViewFormType>
  ) => {
    amplitude.track(savedView ? "Saved edited view" : "Saved view", {
      saved_view_name: values.name,
      saved_view_emoji: values.icon,
    });
    onSave(values);
    onCancel();
    resetForm();
  };

  const initialValues: SavedViewFormType = {
    name: savedView?.name ?? "",
    icon: savedView?.icon ?? "",
  };

  return (
    <Formik<SavedViewFormType>
      initialValues={initialValues}
      validate={validateWithZod(SavedViewFormSchema)}
      onSubmit={handleSave}
      enableReinitialize
    >
      <Modal
        isOpen={isOpen}
        onClose={onCancel}
        closeOnOverlayClick
        size="sm"
        isCentered
      >
        <ModalOverlay />
        <ModalContent as={Form}>
          <ModalBody
            as={Flex}
            direction="column"
            gap={4}
            padding={4}
            className={WEGLOT_APPLY_CLASS}
          >
            <Heading fontWeight={700} color="gray.700" fontSize="md">
              {savedView ? "Edit saved view" : "Save view"}
            </Heading>
            <Flex gap={4} w="100%">
              <Flex direction="column" gap={2}>
                <Text fontSize="sm" color="gray.700">
                  Icon
                </Text>
                <FormikEmojiInput name="icon" />
              </Flex>

              <Flex direction="column" gap={2} w="100%">
                <Text fontSize="sm" color="gray.700">
                  Name
                </Text>
                <InputWithValidationControl
                  id="name"
                  name="name"
                  isRequired={true}
                  inputProps={{
                    maxLength: MAX_NAME_LENGTH,
                    placeholder: "E.g.: Verified",
                  }}
                  marginBottom={0}
                />
                <Text fontSize="sm" color="gray.700">
                  <FieldDerivedValue fieldName="name">
                    {(value: string) => MAX_NAME_LENGTH - value.length}
                  </FieldDerivedValue>
                </Text>
              </Flex>
            </Flex>
          </ModalBody>

          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                amplitude.track("Clicked cancel");
                onCancel();
              }}
            >
              Cancel
            </Button>
            <Button type="submit" size="sm">
              Save view
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Formik>
  );
};
